import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/api';

const ICONS = { rlt: '🔴', cryo: '❄️', hbot: '🫧', 'sauna-communal': '🔥', 'sauna-private': '🌿', biohack: '⚗️', scenar: '⚡', mat: '🧠', nmt: '💪', 'nmt-assessment': '💪' };

const TREATMENT_PHOTOS = {
  rlt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/RLT%20photo.jpeg',
  cryo:             'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/cryo2.jpeg',
  'hbot-30':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-60':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-90':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'sauna-communal': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  'sauna-private':  'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  mat:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/Mat.jpeg',
  nmt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt.png',
  'nmt-assessment': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt1st%20session.png',
};

// NMT sessions are booked by messaging the team on WhatsApp rather than
// through the in-app booking flow. Both the first-session assessment and the
// standard NMT session use this.
const WHATSAPP_NUMBER = '447961834927';
const WHATSAPP_MESSAGE = "Hi, I'd like to book an NMT session.";
const WHATSAPP_SLUGS = ['nmt', 'nmt-assessment'];
const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

function PhotoCard({ t, manual = false }) {
  const photo = TREATMENT_PHOTOS[t.slug];
  if (!photo) return null;
  const isWhatsapp = WHATSAPP_SLUGS.includes(t.slug);
  const inner = (
    <div style={{
      position: 'relative', borderRadius: 16, overflow: 'hidden', height: 200,
      backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'center 35%',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.75) 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 20px' }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', marginBottom: 4 }}>{t.name}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
            {isWhatsapp ? '💬 Tap to book via WhatsApp' : manual ? '📞 Call 01225 743 801 to book' : `${t.duration_mins} min session`}
          </div>
          {!manual && !isWhatsapp && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {t.points_cost && <span style={{ background: '#FF6200', color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>{t.points_cost} pts</span>}
              {t.payg_price_pence && <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>{t.points_cost ? 'or ' : ''}£{(t.payg_price_pence/100).toFixed(0)}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  // NMT → open WhatsApp with a pre-filled booking message (takes priority over
  // the manual phone-booking layout).
  if (isWhatsapp) return <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', marginBottom: 10 }}>{inner}</a>;
  if (manual) return <div style={{ marginBottom: 10 }}>{inner}</div>;
  return <Link to={`/wellness/book/${t.slug}`} style={{ display: 'block', textDecoration: 'none', marginBottom: 10 }}>{inner}</Link>;
}

export default function WellnessPage() {
  const [treatments, setTreatments] = useState([]);
  const [wallet, setWallet]         = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    // Read both from Supabase directly for reliability (same pattern as
    // Dashboard / Wallet / Profile / Bookings). Avoids the backend /treatments
    // and /wallet endpoints which have been flaky.
    Promise.all([
      supabase
        .from('treatments')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .then(({ data }) => data || []),
      supabase.rpc('get_my_wallet_data').then(({ data }) => ({
        balance: data?.balance || 0,
      })),
    ])
      .then(([t, w]) => { setTreatments(t); setWallet(w); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  // MAT is hidden from the Wellness page (filtered here in-app; the row still
  // exists in the treatments table and can be re-enabled by removing this filter).
  const visible  = treatments.filter(t => t.slug !== 'mat');
  // InBody rescans get a dedicated card (own eligibility flow), so keep them out
  // of the generic bookable list.
  const inbody   = visible.find(t => t.slug === 'inbody');
  const bookable = visible.filter(t => t.resource_type !== 'biohack' && t.resource_type !== null && t.slug !== 'inbody');
  const manual   = visible.filter(t => t.resource_type === null);

  return (
    <div className="page">
      <h1 className="page-title">Wellness & Recovery</h1>

      {/* Points balance */}
      <div style={{ background: 'var(--green-pale)', borderRadius: 12, padding: '14px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your Balance</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--green)' }}>{wallet?.balance || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>pts</span></div>
        </div>
        <Link to="/wallet/packs" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '10px 16px' }}>Buy Points</Link>
      </div>

      {/* Biohack feature */}
      <Link to="/wellness/biohack" style={{ display: 'block', marginBottom: 20, textDecoration: 'none' }}>
        <div style={{
          position: 'relative', borderRadius: 16, overflow: 'hidden', height: 180,
          backgroundImage: 'url(https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/biohack.png)',
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.8) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#fff' }}>The Biohack</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', marginTop: 2 }}>From £199 per person</div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#FF6200', letterSpacing: '0.14em', marginTop: 6 }}>HALF DAY EXPERIENCE</div>
          </div>
          <div style={{ position: 'absolute', top: 16, right: 16, color: '#FF6200', fontSize: '1.3rem' }}>→</div>
        </div>
      </Link>

      {/* InBody 770 rescan — self-service 15-min slots. Free for members and
          weekly PT clients, £20 otherwise (handled in the booking flow). */}
      {inbody && (
        <Link to="/wellness/book/inbody" style={{ display: 'block', marginBottom: 20, textDecoration: 'none' }}>
          <div style={{
            position: 'relative', borderRadius: 16, overflow: 'hidden', padding: '18px 20px',
            background: 'linear-gradient(135deg, #0F3D3E 0%, #155E63 100%)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7FD1C4', letterSpacing: '0.14em', marginBottom: 4 }}>BODY COMPOSITION</div>
                <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#fff' }}>InBody 770 Rescan</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', marginTop: 4 }}>Self-service · 15 min · free for members</div>
              </div>
              <div style={{ marginLeft: 14, background: '#fff', borderRadius: 12, padding: 6, flexShrink: 0 }}>
                <img src="/images/inbody770.png" alt="InBody 770 scanner" style={{ display: 'block', height: 72, width: 'auto', objectFit: 'contain' }} />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Bookable treatments */}
      <div className="section-title">Book a Treatment</div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {bookable.map(t => {
          if (TREATMENT_PHOTOS[t.slug]) return <PhotoCard key={t.id} t={t} />;
          return (
            <Link key={t.id} to={`/wellness/book/${t.slug}`} className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none', marginBottom: 10 }}>
              <div style={{ fontSize: '1.8rem', width: 44, textAlign: 'center' }}>{ICONS[t.slug] || '💚'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.name}</div>
                <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem', marginTop: 2 }}>{t.duration_mins} min session</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {t.points_cost && <div className="points-badge" style={{ marginBottom: 4 }}>{t.points_cost} pts</div>}
                {t.payg_price_pence && <div style={{ fontSize: '0.8rem', color: 'var(--grey-500)' }}>{t.points_cost ? 'or ' : ''}£{(t.payg_price_pence / 100).toFixed(0)}</div>}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Manual booking treatments */}
      {manual.length > 0 && (
        <>
          <div className="section-title">Book by Phone / Email</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {manual.map(t => {
              if (TREATMENT_PHOTOS[t.slug]) return <PhotoCard key={t.id} t={t} manual />;
              return (
                <div key={t.id} className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10, opacity: 0.7 }}>
                  <div style={{ fontSize: '1.8rem', width: 44, textAlign: 'center' }}>{ICONS[t.slug] || '💚'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.name}</div>
                    <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem', marginTop: 2 }}>Call 01225 743 801 to book</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {t.points_cost && <div className="points-badge">{t.points_cost} pts</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
